/**
 * Resolution trackers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const resolutionTrackersPack: ToilePack = {
    slug: 'resolution-trackers',
    category: 'season',
    name: 'Resolution trackers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'list', label: 'Resolutions list', subject: 'a page lettered "This Year I Will" with numbered blank lines and checkboxes, a star motif'},
        {key: 'habits', label: 'Habit grid', subject: 'a page lettered "Habit Tracker" with a grid of twelve months by blank rows, a thin border'},
        {key: 'word', label: 'Word of the year', subject: 'a page lettered "My Word for the Year" with a large blank circle and three blank lines beneath'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a vertical page with a gold border and an even margin, the header centred, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin, the header centred, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a vertical page with wide margins, the header centred, print-ready.'},
    ],
    directions: ['for a fitness goal instead', 'as a monthly page', 'for a reading challenge'],
    directionHint: 'try: for a fitness goal instead',
    seo: {
        title: 'Resolution Trackers: List, Habit Grid & Word',
        metaDescription: 'Nine New Year resolution pages — a resolutions list, a twelve-month habit grid, and a word-of-the-year page — headers lettered, lines left blank.',
        h1: 'Resolution trackers: list, habit grid, word of the year',
        tagline: 'Nine pages, three planner formats, three styles each — headers lettered, lines left blank.',
        intro: 'Three January planner pages: a numbered resolutions list, a twelve-month habit-tracking grid, and a word-of-the-year page with a blank circle, each in black-and-gold, confetti-party, and minimal styles. Ask for a fitness focus, a reading-challenge version, or a monthly layout, and the same planner grammar carries it.',
        useCases: [
            'a New Year planner insert',
            'a bullet-journal resolution page',
            'a classroom goal-setting activity',
            'a keepsake word-of-the-year print',
        ],
        faq: [
            {q: 'Are these fillable digital planner pages, or printed pages?', a: 'Printed pages — the lines and boxes are left blank in the image for a pen, not editable text fields.'},
            {q: 'What size do these print at?', a: 'Each page is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a fitness or reading-challenge version?', a: 'Yes — the direction field takes any variation, including a fitness goal or reading-challenge focus.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
